import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Ticket, Plus, MessageSquare, LogOut, Shield, Clock, CheckCircle2, XCircle, Loader2, AlertCircle, LayoutDashboard, Users, Paperclip, Trash2, Edit, Building2, Settings, Link, Unlink, UserCircle, CalendarDays, CalendarClock, ExternalLink } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import CompanySearch from '@/components/CompanySearch';
import RequesterSearch from '@/components/RequesterSearch';
import ProblemTypeSearch from '@/components/ProblemTypeSearch';
import CsvExportButton from '@/components/CsvExportButton';
import ClickUpTaskButton from '@/components/ClickUpTaskButton';
import { getTicketHoursIncludingSubtasks } from '@/lib/ticketHours';

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL;
const API = `${BACKEND_URL}/api`;

const toLocalYYYYMMDD = (dateInput) => {
  if (!dateInput) return '';
  const d = new Date(dateInput);
  if (isNaN(d)) return '';
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${da}`;
};

const PROBLEM_TYPES = [
  { value: 'site_down', label: 'Site Down/Not Loading', priority: 'critical' },
  { value: 'critical_bug', label: 'Critical Bug/Error', priority: 'critical' },
  { value: 'security_concern', label: 'Security Concern', priority: 'critical' },
  { value: 'functionality_broken', label: 'Functionality Not Working', priority: 'high' },
  { value: 'performance_issue', label: 'Performance/Speed Issue', priority: 'high' },
  { value: 'content_update', label: 'Content Update Request', priority: 'medium' },
  { value: 'design_change', label: 'Design/Layout Change', priority: 'medium' },
  { value: 'seo_update', label: 'SEO/Meta Updates', priority: 'low' },
  { value: 'feature_request', label: 'New Feature Request', priority: 'low' },
  { value: 'general_question', label: 'General Question/Support', priority: 'low' },
  { value: 'other', label: 'Other', priority: 'medium' }
];

const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tickets, setTickets] = useState([]);
  const [workload, setWorkload] = useState({ today: [], tomorrow: [], this_week: [] });
  const [assignmentInbox, setAssignmentInbox] = useState([]);
  const [assignmentCompanies, setAssignmentCompanies] = useState({});
  const [dismissingAssignments, setDismissingAssignments] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [timeLogDialogOpen, setTimeLogDialogOpen] = useState(false);

  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    problem_type: '',
    website_url: '',
    company_id: '',
    requested_by: '',
    due_date: '',
    time_estimate: '',
    created_for_user_id: ''
  });
  const [companies, setCompanies] = useState([]);
  const [addCompanyOpen, setAddCompanyOpen] = useState(false);
  const [newCompanyForm, setNewCompanyForm] = useState({ name: '', url: '', notes: '' });
  const [commentText, setCommentText] = useState('');
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [commentLoading, setCommentLoading] = useState(false);
  const [timeLog, setTimeLog] = useState({ hours: '', description: '' });
  const [timeLogLoading, setTimeLogLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');

  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');

  const [editingTimeLogId, setEditingTimeLogId] = useState(null);
  const [editingTimeLogForm, setEditingTimeLogForm] = useState({ hours: '', description: '' });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [datePreset, setDatePreset] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  // ClickUp OAuth connection state
  const [clickupConnected, setClickupConnected] = useState(false);
  const [clickupConnecting, setClickupConnecting] = useState(false);

  // Subtask state
  const [subtaskInput, setSubtaskInput] = useState('');
  const [subtaskDescription, setSubtaskDescription] = useState('');
  const [subtaskAssignee, setSubtaskAssignee] = useState('');
  const [subtaskDueDate, setSubtaskDueDate] = useState('');
  const [subtaskTimeEstimate, setSubtaskTimeEstimate] = useState('');
  const [subtaskProblemType, setSubtaskProblemType] = useState('');
  const [subtaskLoading, setSubtaskLoading] = useState(false);
  const [showSubtaskForm, setShowSubtaskForm] = useState(false);
  const [editingSubtaskId, setEditingSubtaskId] = useState(null);
  const [editingSubtaskForm, setEditingSubtaskForm] = useState({ title: '', description: '', assignee_email: '', due_date: '', time_estimate: '', problem_type: '', status: '' });
  const [teamMembers, setTeamMembers] = useState([]);
  const [subtaskCommentText, setSubtaskCommentText] = useState({});
  const [subtaskCommentLoading, setSubtaskCommentLoading] = useState({});
  const [expandedSubtaskComments, setExpandedSubtaskComments] = useState({});

  const handleDatePreset = (val) => {
    setDatePreset(val);
    if (val === 'all') { setDateRange({ start: '', end: '' }); return; }
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const fmt = d => toLocalYYYYMMDD(d);
    const d = new Date(today);
    if (val === 'today') { setDateRange({ start: fmt(today), end: fmt(today) }); }
    else if (val === 'this_week') { const day = d.getDay(); d.setDate(d.getDate() - (day === 0 ? 6 : day - 1)); setDateRange({ start: fmt(d), end: fmt(today) }); }
    else if (val === 'this_month') { setDateRange({ start: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), end: fmt(today) }); }
    else if (val === 'last_month') { const s = new Date(today.getFullYear(), today.getMonth() - 1, 1); const e = new Date(today.getFullYear(), today.getMonth(), 0); setDateRange({ start: fmt(s), end: fmt(e) }); }
    else if (val === 'last_7') { const s = new Date(today); s.setDate(s.getDate() - 6); setDateRange({ start: fmt(s), end: fmt(today) }); }
    else if (val === 'last_30') { const s = new Date(today); s.setDate(s.getDate() - 29); setDateRange({ start: fmt(s), end: fmt(today) }); }
  };

  useEffect(() => {
    fetchTickets();
    fetchCompanies();
    fetchWorkload();
    fetchAssignmentInbox();
    if (user?.role === 'admin' || user?.role === 'subadmin') {
      fetchUsers();
    }
    // Check ClickUp connection status
    axios.get(`${API}/auth/clickup/status`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => setClickupConnected(r.data.connected))
      .catch(() => {});
    // Handle redirect back from ClickUp OAuth
    const params = new URLSearchParams(window.location.search);
    if (params.get('clickup_connected') === '1') {
      toast.success('ClickUp connected! Your tasks and comments will now show your profile.');
      setClickupConnected(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Auto-open ticket from URL query param (e.g. ?ticketId=xxx)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ticketId = params.get('ticketId');
    if (ticketId && tickets.length > 0) {
      handleViewTicket(ticketId);
      // Clear the query param
      navigate('/dashboard', { replace: true });
    }
  }, [location.search, tickets]);

  // Update description and title state when ticket is selected
  useEffect(() => {
    if (selectedTicket) {
      setEditedDescription(selectedTicket.description);
      setIsEditingDescription(false);
      setEditedTitle(selectedTicket.title);
      setIsEditingTitle(false);
    }
  }, [selectedTicket]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const handleConnectClickup = async () => {
    setClickupConnecting(true);
    try {
      const res = await axios.get(`${API}/auth/clickup/connect`, getAuthHeaders());
      window.location.href = res.data.auth_url;
    } catch (err) {
      toast.error('Failed to start ClickUp connection');
      setClickupConnecting(false);
    }
  };

  const handleDisconnectClickup = async () => {
    if (!window.confirm('Disconnect ClickUp? Tasks/comments will fall back to the system account.')) return;
    try {
      await axios.delete(`${API}/auth/clickup/disconnect`, getAuthHeaders());
      setClickupConnected(false);
      toast.success('ClickUp disconnected.');
    } catch (err) {
      toast.error('Failed to disconnect ClickUp');
    }
  };

  const openTrackingPage = async ticketId => {
    const trackingWindow = window.open('', '_blank');
    try {
      const response = await axios.get(`${API}/tickets/${ticketId}/tracking-url`, getAuthHeaders());
      if (trackingWindow) trackingWindow.location.href = response.data.tracking_url;
      else window.open(response.data.tracking_url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      if (trackingWindow) trackingWindow.close();
      toast.error(error.response?.data?.detail || 'Failed to open tracking page');
    }
  };

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API}/tickets`, getAuthHeaders());
      setTickets(response.data);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/task-assignees`, getAuthHeaders());
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`, getAuthHeaders());
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to load companies');
    }
  };

  const fetchWorkload = async () => {
    try {
      const response = await axios.get(`${API}/workload/board`, getAuthHeaders());
      setWorkload(response.data);
    } catch {
      console.error('Failed to load personal workload');
    }
  };

  const fetchAssignmentInbox = async () => {
    try {
      const response = await axios.get(`${API}/clickup-assignment-inbox`, getAuthHeaders());
      setAssignmentInbox(response.data || []);
    } catch {
      console.error('Failed to load ClickUp assignment inbox');
    }
  };

  const linkAssignmentToCompany = async assignmentId => {
    const companyId = assignmentCompanies[assignmentId];
    if (!companyId) return toast.error('Select the related company');
    try {
      await axios.post(
        `${API}/clickup-assignment-inbox/${assignmentId}/link`,
        { company_id: companyId },
        getAuthHeaders()
      );
      toast.success('ClickUp task linked to the company and added to your tickets');
      setAssignmentInbox(items => items.filter(item => item.id !== assignmentId));
      fetchTickets();
      fetchWorkload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link ClickUp task');
    }
  };

  const dismissAssignment = async assignmentId => {
    try {
      await axios.delete(`${API}/clickup-assignment-inbox/${assignmentId}`, getAuthHeaders());
      setAssignmentInbox(items => items.filter(item => item.id !== assignmentId));
      toast.success('Notification dismissed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to dismiss notification');
    }
  };

  const dismissAllAssignments = async () => {
    setDismissingAssignments(true);
    try {
      const response = await axios.delete(`${API}/clickup-assignment-inbox`, getAuthHeaders());
      setAssignmentInbox([]);
      toast.success(`${response.data.dismissed_count || 0} notifications dismissed`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to dismiss notifications');
    } finally {
      setDismissingAssignments(false);
    }
  };

  const handleCreateCompanyInline = async (e) => {
    e.preventDefault();
    if (!newCompanyForm.name.trim()) { toast.error('Company name is required'); return; }
    try {
      const res = await axios.post(`${API}/companies`, newCompanyForm, getAuthHeaders());
      toast.success(`Company "${res.data.name}" created`);
      setAddCompanyOpen(false);
      setNewCompanyForm({ name: '', url: '', notes: '' });
      await fetchCompanies();

      if (viewDialogOpen && selectedTicket) {
        handleUpdateTicketField(selectedTicket.id, 'company_id', res.data.id);
      } else {
        setNewTicket(prev => ({ ...prev, company_id: res.data.id }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create company');
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.due_date) {
      toast.error("Due Date is required");
      return;
    }
    if (!newTicket.problem_type) {
      toast.error("Problem Type is required");
      return;
    }
    try {
      let url = `${API}/tickets`;
      if (newTicket.created_for_user_id) {
        url += `?created_for_user_id=${newTicket.created_for_user_id}`;
      }
      const payload = {
        title: newTicket.title,
        description: newTicket.description,
        problem_type: newTicket.problem_type,
        website_url: newTicket.website_url || undefined,
        company_id: newTicket.company_id || undefined,
        requested_by: newTicket.requested_by || undefined,
      };
      if (newTicket.due_date) {
        payload.due_date = new Date(newTicket.due_date + 'T00:00:00').toISOString();
      }
      if (newTicket.time_estimate) {
        payload.time_estimate = parseFloat(newTicket.time_estimate);
      }

      await axios.post(url, payload, getAuthHeaders());
      toast.success('Ticket created successfully!');
      setNewTicket({
        title: '',
        description: '',
        problem_type: '',
        website_url: '',
        company_id: '',
        requested_by: '',
        due_date: '',
        time_estimate: '',
        created_for_user_id: ''
      });
      setCreateDialogOpen(false);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to create ticket');
    }
  };

  const handleViewTicket = async (ticketId) => {
    try {
      const response = await axios.get(`${API}/tickets/${ticketId}`, getAuthHeaders());
      setSelectedTicket(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  const handleUpdateTitle = async () => {
    if (!editedTitle.trim()) return;
    try {
      const response = await axios.patch(
        `${API}/tickets/${selectedTicket.id}`,
        { title: editedTitle },
        getAuthHeaders()
      );
      toast.success('Title updated');
      setIsEditingTitle(false);
      fetchTickets();
      setSelectedTicket(response.data);
    } catch (error) {
      toast.error('Failed to update title');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;

    setCommentLoading(true);
    try {
      const response = await axios.post(
        `${API}/tickets/${selectedTicket.id}/comments`,
        { text: commentText, attachments: commentAttachments },
        getAuthHeaders()
      );
      setSelectedTicket(response.data);
      setCommentText('');
      setCommentAttachments([]);
      toast.success('Comment added');
      fetchTickets();
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setCommentLoading(false);
    }
  };

  const handleAddTimeLog = async (e) => {
    e.preventDefault();
    if (!timeLog.hours || !timeLog.description.trim()) return;

    setTimeLogLoading(true);
    try {
      const response = await axios.post(
        `${API}/tickets/${selectedTicket.id}/time-log`,
        { hours: parseFloat(timeLog.hours), description: timeLog.description },
        getAuthHeaders()
      );
      setSelectedTicket(response.data);
      setTimeLog({ hours: '', description: '' });
      setTimeLogDialogOpen(false);
      toast.success('Time logged successfully');
      fetchTickets();
    } catch (error) {
      toast.error('Failed to log time');
    } finally {
      setTimeLogLoading(false);
    }
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    try {
      await axios.patch(
        `${API}/tickets/${ticketId}`,
        { status: newStatus },
        getAuthHeaders()
      );
      toast.success('Status updated');
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUpdateTicketField = async (ticketId, field, value) => {
    try {
      const payload = { [field]: value };
      if (field === 'due_date' && value) {
        payload.due_date = new Date(value + 'T00:00:00').toISOString();
      }
      await axios.patch(
        `${API}/tickets/${ticketId}`,
        payload,
        getAuthHeaders()
      );
      toast.success(`${field.replace('_', ' ')} updated`);
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, [field]: payload.due_date || value });
      }
    } catch (error) {
      toast.error(`Failed to update ${field.replace('_', ' ')}`);
    }
  };

  const handleUpdateDescription = async () => {
    try {
      const response = await axios.patch(
        `${API}/tickets/${selectedTicket.id}`,
        { description: editedDescription },
        getAuthHeaders()
      );
      toast.success('Description updated');
      setSelectedTicket(response.data);
      setIsEditingDescription(false);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to update description');
    }
  };

  const handleUpdateComment = async (commentId) => {
    try {
      const response = await axios.patch(
        `${API}/tickets/${selectedTicket.id}/comments/${commentId}`,
        { text: editingCommentText },
        getAuthHeaders()
      );
      toast.success('Comment updated');
      setSelectedTicket(response.data);
      setEditingCommentId(null);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to update comment');
    }
  };

  const handleUpdateTimeLog = async (logId) => {
    const targetForm = editingTimeLogForm;
    if (!targetForm.hours || !targetForm.description.trim()) return;
    try {
      const response = await axios.patch(
        `${API}/tickets/${selectedTicket.id}/time-log/${logId}`,
        { hours: parseFloat(targetForm.hours), description: targetForm.description },
        getAuthHeaders()
      );
      toast.success('Time log updated');
      setSelectedTicket(response.data);
      setEditingTimeLogId(null);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to update time log');
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    try {
      await axios.delete(`${API}/tickets/${ticketId}`, getAuthHeaders());
      toast.success('Ticket deleted successfully');
      setViewDialogOpen(false);
      setSelectedTicket(null);
      setShowDeleteConfirm(false);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to delete ticket');
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Simple validation
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error("File size must be less than 5MB");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          ...getAuthHeaders().headers,
          "Content-Type": "multipart/form-data"
        }
      });
      setCommentAttachments([...commentAttachments, response.data.url]);
      toast.success("File attached");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload file");
    }
  };

  const removeAttachment = (index) => {
    const newAttachments = [...commentAttachments];
    newAttachments.splice(index, 1);
    setCommentAttachments(newAttachments);
  };

  const getStatusBadge = (status) => {
    const variants = {
      open: { color: 'bg-blue-600 text-white', icon: Clock },
      in_progress: { color: 'bg-yellow-500 text-white', icon: Loader2 },
      resolved: { color: 'bg-green-600 text-white', icon: CheckCircle2 },
      closed: { color: 'bg-gray-600 text-white', icon: XCircle }
    };

    const variant = variants[status] || variants.open;
    const Icon = variant.icon;

    return (
      <Badge className={`${variant.color} flex items-center gap-1 font-bold uppercase`}>
        <Icon className="w-3 h-3" />
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      critical: { color: 'bg-red-600 text-white border-red-700', icon: AlertCircle },
      high: { color: 'bg-orange-500 text-white border-orange-600', icon: AlertCircle },
      medium: { color: 'bg-yellow-500 text-white border-yellow-600', icon: Clock },
      low: { color: 'bg-green-500 text-white border-green-600', icon: CheckCircle2 }
    };

    const variant = variants[priority] || variants.medium;
    const Icon = variant.icon;

    return (
      <Badge className={`${variant.color} flex items-center gap-1 border font-bold uppercase`}>
        <Icon className="w-3 h-3" />
        {priority}
      </Badge>
    );
  };

  const isAdminOrSubadmin = user?.role === 'admin' || user?.role === 'subadmin';

  // Load team members for subtask assignee picker (admins only)
  useEffect(() => {
    if (isAdminOrSubadmin) {
      axios.get(`${API}/users`, getAuthHeaders())
        .then(r => setTeamMembers((r.data || []).filter(u => u.role === 'admin' || u.role === 'subadmin')))
        .catch(() => {});
    }
  }, [isAdminOrSubadmin]);

  // Subtask handlers
  const handleAddSubtask = async () => {
    if (!subtaskInput.trim() || !selectedTicket) return;
    setSubtaskLoading(true);
    try {
      const res = await axios.post(
        `${API}/tickets/${selectedTicket.id}/subtasks`,
        { 
          title: subtaskInput.trim(), 
          description: subtaskDescription.trim() || null,
          assignee_email: subtaskAssignee || null, 
          due_date: subtaskDueDate || null,
          time_estimate: subtaskTimeEstimate ? parseFloat(subtaskTimeEstimate) : null,
          tags: subtaskProblemType ? [subtaskProblemType] : []
        },
        getAuthHeaders()
      );
      setSelectedTicket(res.data);
      setTickets(prev => prev.map(t => t.id === res.data.id ? res.data : t));
      setSubtaskInput(''); setSubtaskDescription(''); setSubtaskAssignee(''); setSubtaskDueDate(''); setSubtaskTimeEstimate(''); setSubtaskProblemType('');
      setShowSubtaskForm(false);
      toast.success('Subtask added');
    } catch { toast.error('Failed to add subtask'); }
    finally { setSubtaskLoading(false); }
  };

  const handleToggleSubtaskStatus = async (subtask) => {
    const nextStatus = subtask.status === 'done' ? 'open' : subtask.status === 'open' ? 'in_progress' : 'done';
    try {
      const res = await axios.patch(
        `${API}/tickets/${selectedTicket.id}/subtasks/${subtask.id}`,
        { status: nextStatus }, getAuthHeaders()
      );
      setSelectedTicket(res.data);
      setTickets(prev => prev.map(t => t.id === res.data.id ? res.data : t));
    } catch { toast.error('Failed to update subtask'); }
  };

  const handleUpdateSubtask = async (subtaskId) => {
    try {
      const res = await axios.patch(
        `${API}/tickets/${selectedTicket.id}/subtasks/${subtaskId}`,
        { 
          title: editingSubtaskForm.title, 
          description: editingSubtaskForm.description || null,
          assignee_email: editingSubtaskForm.assignee_email || null, 
          due_date: editingSubtaskForm.due_date || null,
          time_estimate: editingSubtaskForm.time_estimate ? parseFloat(editingSubtaskForm.time_estimate) : null,
          tags: editingSubtaskForm.problem_type ? [editingSubtaskForm.problem_type] : [],
          status: editingSubtaskForm.status || undefined
        },
        getAuthHeaders()
      );
      setSelectedTicket(res.data);
      setTickets(prev => prev.map(t => t.id === res.data.id ? res.data : t));
      setEditingSubtaskId(null);
      toast.success('Subtask updated');
    } catch { toast.error('Failed to update subtask'); }
  };

  const handleDeleteSubtask = async (subtaskId) => {
    try {
      const res = await axios.delete(`${API}/tickets/${selectedTicket.id}/subtasks/${subtaskId}`, getAuthHeaders());
      setSelectedTicket(res.data);
      setTickets(prev => prev.map(t => t.id === res.data.id ? res.data : t));
      toast.success('Subtask deleted');
    } catch { toast.error('Failed to delete subtask'); }
  };

  const handleAddSubtaskComment = async (subtaskId) => {
    const text = subtaskCommentText[subtaskId]?.trim();
    if (!text) return;
    setSubtaskCommentLoading(prev => ({ ...prev, [subtaskId]: true }));
    try {
      const res = await axios.post(
        `${API}/tickets/${selectedTicket.id}/subtasks/${subtaskId}/comments`,
        { text },
        getAuthHeaders()
      );
      setSelectedTicket(res.data);
      setSubtaskCommentText(prev => ({ ...prev, [subtaskId]: '' }));
      toast.success('Comment added');
    } catch { toast.error('Failed to add comment'); }
    finally { setSubtaskCommentLoading(prev => ({ ...prev, [subtaskId]: false })); }
  };

  // Tickets filtered by date range — always based on due_date
  const dateFilteredTickets = tickets.filter(t => {
    if (!dateRange.start && !dateRange.end) return true;

    // Always use due_date for filtering; skip tickets with no due date when a filter is active
    const dateToCompare = toLocalYYYYMMDD(t.due_date);
    if (!dateToCompare) return false;

    if (dateRange.start && dateToCompare < dateRange.start) return false;
    if (dateRange.end && dateToCompare > dateRange.end) return false;
    return true;
  });

  // Compute admin stats from date-filtered tickets
  const openCount = dateFilteredTickets.filter(t => t.status === 'open').length;
  const inProgressCount = dateFilteredTickets.filter(t => t.status === 'in_progress').length;
  const resolvedCount = dateFilteredTickets.filter(t => t.status === 'resolved' || t.status === 'closed').length;
  const totalHours = dateFilteredTickets.reduce((sum, t) => sum + (t.total_hours_spent || 0), 0);

  // A completed ticket that still has open subtasks — always pinned to the top
  const hasOpenSubtasks = (t) =>
    (t.status === 'resolved' || t.status === 'closed') &&
    t.subtasks?.some(s => s.status !== 'done');

  // Filtered tickets (date + search + status — what shows in the card list)
  // Completed tickets with open subtasks are always included regardless of status filter
  const filteredTickets = [...dateFilteredTickets]
    .filter(t => {
      const matchesSearch = !searchQuery ||
        t.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.created_by_email?.toLowerCase().includes(searchQuery.toLowerCase());
      // Always show completed tickets that still have open subtasks (pinned)
      const isPinned = hasOpenSubtasks(t);
      const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
      return matchesSearch && (isPinned || matchesStatus);
    })
    .sort((a, b) => {
      // Pinned tickets sort first (priority 0)
      const statusOrder = { open: 1, in_progress: 2, resolved: 3, closed: 4 };
      const orderA = hasOpenSubtasks(a) ? 0 : (statusOrder[a.status] || 5);
      const orderB = hasOpenSubtasks(b) ? 0 : (statusOrder[b.status] || 5);
      if (orderA !== orderB) return orderA - orderB;

      const dateA = new Date(a.due_date || 0);
      const dateB = new Date(b.due_date || 0);
      return dateB - dateA;
    });


  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center min-h-[64px] py-2 gap-4">
            <div className="flex items-center gap-3 shrink-0">
              <img src="/favicon.png" alt="W." className="w-10 h-10 rounded-full" />
              <div>
                <p className="text-[10px] text-gray-500 font-bold ml-0.5 tracking-wider uppercase">Support Hub</p>
              </div>
            </div>

            <div className="flex items-center gap-2 justify-end">
              {clickupConnected ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDisconnectClickup}
                  className="flex items-center gap-1.5 border-green-300 text-green-700 hover:bg-green-50 text-xs"
                  title="ClickUp Connected — click to disconnect"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  ClickUp
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleConnectClickup}
                  disabled={clickupConnecting}
                  className="flex items-center gap-1.5 border-violet-300 text-violet-700 hover:bg-violet-50 text-xs"
                  title="Connect your ClickUp account"
                >
                  {clickupConnecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link className="w-3.5 h-3.5" />}
                  {clickupConnecting ? 'Connecting...' : 'Connect ClickUp'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {assignmentInbox.length > 0 && (
          <Card className="mb-6 border-violet-200 bg-violet-50/50">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Link className="w-4 h-4 text-violet-600" />
                  ClickUp Tasks Waiting for Company
                  <Badge className="bg-violet-600">{assignmentInbox.length}</Badge>
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={dismissAllAssignments} disabled={dismissingAssignments} className="self-start sm:self-auto text-gray-500 hover:text-red-600">
                  {dismissingAssignments ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5 mr-1.5" />}
                  Dismiss all
                </Button>
              </div>
              <CardDescription>These tasks were assigned to you in ClickUp. Select the related company to add them to your workload.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {assignmentInbox.map(item => (
                <div key={item.id} className="bg-white border border-violet-200 rounded-lg p-4 flex flex-col lg:flex-row lg:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{item.title}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Due {item.due_date ? new Date(item.due_date).toLocaleDateString() : 'date not set'}
                    </p>
                  </div>
                  <Select value={assignmentCompanies[item.id] || ''} onValueChange={value => setAssignmentCompanies(current => ({ ...current, [item.id]: value }))}>
                    <SelectTrigger className="w-full lg:w-64 bg-white"><SelectValue placeholder="Which company?" /></SelectTrigger>
                    <SelectContent>
                      {companies.map(company => <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button onClick={() => linkAssignmentToCompany(item.id)}>Assign to Company</Button>
                  <Button variant="outline" onClick={() => dismissAssignment(item.id)} className="text-gray-600 hover:text-red-600">Dismiss</Button>
                  <a href={`https://app.clickup.com/t/${item.clickup_task_id}`} target="_blank" rel="noreferrer">
                    <Button variant="outline" size="icon"><ExternalLink className="w-4 h-4" /></Button>
                  </a>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><CalendarClock className="w-5 h-5 text-indigo-600" />My Scheduled Work</h2>
              <p className="text-sm text-gray-500">Tickets and maintenance assigned to you</p>
            </div>
          </div>
          <div className="grid lg:grid-cols-3 gap-4">
            {[
              ['Today', workload.today || [], 'border-red-200'],
              ['Tomorrow', workload.tomorrow || [], 'border-amber-200'],
              ['This Week', workload.this_week || [], 'border-indigo-200'],
            ].map(([label, items, border]) => (
              <Card key={label} className={border}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center justify-between">{label}<Badge variant="secondary">{items.length}</Badge></CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {items.length ? items.map(item => (
                    <button key={`${item.type}-${item.id}`} onClick={() => navigate(item.path)} className="w-full border rounded-lg p-3 text-left hover:border-indigo-300 hover:bg-indigo-50/40">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold">{item.title}</p>
                        <Badge variant="outline" className="text-[10px] shrink-0">{item.type.replaceAll('_', ' ')}</Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{item.due_date} · {Math.round(Number(item.estimate_hours || 0) * 60)} min</p>
                    </button>
                  )) : <p className="text-sm text-gray-400 py-4 text-center">No scheduled work</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Admin Stats Row */}
        {isAdminOrSubadmin && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="border-blue-200 bg-blue-50/50 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter(statusFilter === 'open' ? 'all' : 'open')}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Open</p>
                  <p className="text-3xl font-bold text-blue-700 mt-1">{openCount}</p>
                </div>
                <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-yellow-200 bg-yellow-50/50 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter(statusFilter === 'in_progress' ? 'all' : 'in_progress')}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-yellow-600 uppercase tracking-wide">In Progress</p>
                  <p className="text-3xl font-bold text-yellow-700 mt-1">{inProgressCount}</p>
                </div>
                <div className="h-10 w-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Loader2 className="w-5 h-5 text-yellow-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50/50 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter(statusFilter === 'resolved' ? 'all' : 'resolved')}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-green-600 uppercase tracking-wide">Resolved</p>
                  <p className="text-3xl font-bold text-green-700 mt-1">{resolvedCount}</p>
                </div>
                <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-purple-200 bg-purple-50/50">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-purple-600 uppercase tracking-wide">Total Hours</p>
                  <p className="text-3xl font-bold text-purple-700 mt-1">{totalHours.toFixed(1)}h</p>
                </div>
                <div className="h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Support Tickets</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {filteredTickets.length} of {dateFilteredTickets.length} tickets
              {(dateRange.start || dateRange.end) && <span className="ml-1 text-indigo-600 font-medium">· {datePreset !== 'all' && datePreset !== 'custom' ? datePreset.replace(/_/g, ' ') : 'custom range'}</span>}
              {statusFilter !== 'all' && <span className="ml-1 text-blue-600 font-medium">· {statusFilter.replace('_', ' ')}</span>}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <CsvExportButton
              filename="support-tickets"
              rows={filteredTickets}
              columns={[
                { label: 'Ticket ID', value: 'id' },
                { label: 'Title', value: 'title' },
                { label: 'Company', value: row => row.company_name || row.company_id || '' },
                { label: 'Status', value: 'status' },
                { label: 'Priority', value: 'priority' },
                { label: 'Problem Type', value: 'problem_type' },
                { label: 'Due Date', value: 'due_date' },
                { label: 'Hours', value: 'total_hours_spent' },
                { label: 'Created By', value: 'created_by_email' },
              ]}
            />

            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-ticket-button" className="bg-black hover:bg-gray-800 flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  New Ticket
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="create-ticket-dialog" className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Create Support Ticket</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateTicket} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="problem_type">Problem Type</Label>
                    <ProblemTypeSearch
                      value={newTicket.problem_type}
                      onChange={(ptValue, ptObj) => setNewTicket({ ...newTicket, problem_type: ptValue })}
                      placeholder="Select problem type..."
                      allowCreate={true}
                    />
                    <p className="text-xs text-gray-500">Priority is automatically assigned based on problem type</p>
                  </div>

                  {user?.role === 'admin' && (
                    <div className="space-y-2">
                      <Label htmlFor="created_for_user">Assignee / Create on behalf of</Label>
                      <Select
                        value={newTicket.created_for_user_id || 'self'}
                        onValueChange={(value) => setNewTicket({ ...newTicket, created_for_user_id: value === 'self' ? '' : value })}
                      >
                        <SelectTrigger data-testid="user-select">
                          <SelectValue placeholder="Select the task assignee" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="self">Assign to myself</SelectItem>
                          {users.map((userItem) => (
                            <SelectItem key={userItem.id} value={userItem.id}>
                              {userItem.email}{userItem.company_name ? ` - ${userItem.company_name}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500">This person becomes the app owner and ClickUp assignee.</p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="company_id">Company (Optional)</Label>
                    <CompanySearch
                      value={newTicket.company_id || null}
                      onChange={(id, company) => {
                        setNewTicket({ ...newTicket, company_id: id || '' });
                        if (company) fetchCompanies();
                      }}
                      companies={companies}
                      placeholder="Search or create company..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="requested_by">Requested By (Optional)</Label>
                    <RequesterSearch
                      value={newTicket.requested_by || null}
                      onChange={(id) => setNewTicket({ ...newTicket, requested_by: id || '' })}
                      placeholder="Who requested this..."
                    />
                    <p className="text-xs text-gray-500">Account executive or employee who submitted this request</p>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-1 space-y-2">
                      <Label htmlFor="due_date">Due Date <span className="text-red-500">*</span></Label>
                      <Input
                        id="due_date"
                        required
                        type="date"
                        data-testid="ticket-due-date-input"
                        value={newTicket.due_date}
                        onChange={(e) => setNewTicket({ ...newTicket, due_date: e.target.value })}
                      />
                    </div>
                    <div className="flex-1 space-y-2">
                      <Label htmlFor="time_estimate">Time Estimate (Hours)</Label>
                      <Input
                        id="time_estimate"
                        type="number"
                        step="0.5"
                        min="0"
                        placeholder="e.g. 2.5"
                        value={newTicket.time_estimate}
                        onChange={(e) => setNewTicket({ ...newTicket, time_estimate: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      data-testid="ticket-title-input"
                      placeholder="Brief description of the issue"
                      value={newTicket.title}
                      onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      data-testid="ticket-description-input"
                      placeholder="Provide detailed information about your issue"
                      value={newTicket.description}
                      onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                      rows={4}
                      required
                    />
                  </div>

                  <Button type="submit" data-testid="submit-ticket-button" className="w-full bg-black hover:bg-gray-800">
                    Create Ticket
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Time Range Filter */}
        <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-white rounded-lg border border-gray-200">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-1">Period:</span>
          {[['all', 'All Time'], ['today', 'Today'], ['last_7', 'Last 7 Days'], ['this_week', 'This Week'], ['this_month', 'This Month'], ['last_month', 'Last Month'], ['last_30', 'Last 30 Days']].map(([val, label]) => (
            <button
              key={val}
              onClick={() => handleDatePreset(val)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${datePreset === val ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
            >{label}</button>
          ))}
          <span className="text-gray-300 mx-1">|</span>
          <input type="date" value={dateRange.start}
            onChange={e => { setDatePreset('custom'); setDateRange(r => ({ ...r, start: e.target.value })); }}
            className="text-xs border border-gray-200 rounded px-2 py-1 h-7" />
          <span className="text-gray-400 text-xs">to</span>
          <input type="date" value={dateRange.end}
            onChange={e => { setDatePreset('custom'); setDateRange(r => ({ ...r, end: e.target.value })); }}
            className="text-xs border border-gray-200 rounded px-2 py-1 h-7" />
        </div>

        {/* Search + Filter bar */}
        <div className="flex flex-col sm:flex-row gap-2 mb-6">
          <div className="relative flex-1">
            <Input
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-white pr-8"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Clear search"
              >
                <XCircle className="w-4 h-4" />
              </button>
            )}
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-44 bg-white">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : filteredTickets.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Ticket className="w-12 h-12 text-gray-400 mb-4" />
              <p className="text-gray-600 font-medium">{tickets.length === 0 ? 'No tickets yet' : 'No tickets match your search'}</p>
              <p className="text-sm text-gray-500 mt-1">{tickets.length === 0 ? 'Create a new ticket to get started.' : 'Try adjusting your filters.'}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3" data-testid="tickets-list">
            {filteredTickets.map((ticket) => {
              const isOverdue = ticket.due_date && new Date(ticket.due_date) < new Date() && ticket.status !== 'resolved' && ticket.status !== 'closed';
              const isPinned = hasOpenSubtasks(ticket);
              const openSubtaskCount = isPinned ? ticket.subtasks.filter(s => s.status !== 'done').length : 0;
              return (
                <Card
                  key={ticket.id}
                  data-testid={`ticket-card-${ticket.id}`}
                  className={`hover:shadow-md transition-all cursor-pointer border-l-4 ${
                    isPinned ? 'border-l-orange-400 ring-1 ring-orange-200' :
                    ticket.status === 'open' ? 'border-l-blue-400' :
                    ticket.status === 'in_progress' ? 'border-l-yellow-400' :
                    ticket.status === 'resolved' ? 'border-l-green-400' : 'border-l-gray-300'
                  }`}
                  onClick={() => handleViewTicket(ticket.id)}
                >
                  {isPinned && (
                    <div className="flex items-center gap-1.5 px-4 pt-2.5 pb-0">
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-600 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded-full">
                        <AlertCircle className="w-3 h-3" />
                        {openSubtaskCount} open {openSubtaskCount === 1 ? 'subtask' : 'subtasks'} remaining
                      </span>
                    </div>
                  )}
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="font-semibold text-gray-900 truncate">{ticket.title}</h3>
                          {getPriorityBadge(ticket.priority)}
                          {ticket.problem_type?.split(',').map(pt => (
                            <Badge key={pt} variant="outline" className="text-xs bg-gray-50 text-gray-600 truncate max-w-[150px]">
                              {pt.trim()}
                            </Badge>
                          ))}
                          {ticket.is_paid_support && (
                            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium border border-emerald-200">Paid</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 line-clamp-1 mb-2">{ticket.description}</p>
                        <div className="flex items-center gap-3 flex-wrap text-xs text-gray-400">
                          <span>{ticket.created_by_email}</span>
                          {ticket.company_name && (
                            <span className="flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-full text-gray-600">
                              <Building2 className="w-3 h-3" />{ticket.company_name}
                            </span>
                          )}
                          {ticket.requested_by_name && (
                            <span className="flex items-center gap-1 bg-indigo-50 px-2 py-0.5 rounded-full text-indigo-600">
                              <UserCircle className="w-3 h-3" />{ticket.requested_by_name}
                            </span>
                          )}
                          {getTicketHoursIncludingSubtasks(ticket) > 0 && (
                            <span className="flex items-center gap-1 text-orange-500 font-medium">
                              <Clock className="w-3 h-3" />{getTicketHoursIncludingSubtasks(ticket)}h
                            </span>
                          )}
                          {ticket.comments?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" />{ticket.comments.length}
                            </span>
                          )}
                          {ticket.subtasks?.length > 0 && (
                            <span className="flex items-center gap-1.5 text-gray-500">
                              <span className="text-xs font-medium">
                                {ticket.subtasks.filter(s => s.status === 'done').length}/{ticket.subtasks.length} subtasks
                              </span>
                              <span className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden inline-block align-middle">
                                <span
                                  className="h-full bg-green-400 rounded-full block"
                                  style={{ width: `${(ticket.subtasks.filter(s => s.status === 'done').length / ticket.subtasks.length) * 100}%` }}
                                />
                              </span>
                            </span>
                          )}
                          {ticket.due_date && (
                            <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                              {isOverdue ? '⚠ Overdue:' : 'Due:'} {new Date(ticket.due_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        {getStatusBadge(ticket.status)}
                        <ClickUpTaskButton taskId={ticket.clickup_task_id} iconOnly className="h-7 w-7 p-0" />
                        <span className="text-xs text-gray-400">{new Date(ticket.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* View Ticket Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-[100] bg-black/80 overflow-y-auto">
            <div className="min-h-full flex items-center justify-center p-4 sm:p-8">
              <DialogPrimitive.Content 
                data-testid="view-ticket-dialog"
                className="relative w-full max-w-[95vw] sm:max-w-3xl bg-white shadow-xl sm:rounded-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200"
              >
                {selectedTicket && (
                  <div className="flex flex-col relative bg-white">
                    <div className="p-4 sm:p-6 pb-3 border-b border-gray-100 sticky top-0 bg-white z-20 shadow-sm">
                <DialogHeader className="space-y-4">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-xs">#{selectedTicket.id.slice(-6)}</span>
                        <span>•</span>
                        <span>{new Date(selectedTicket.created_at).toLocaleDateString()}</span>
                      </div>

                      {/* Admin Actions Top Right */}
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => openTrackingPage(selectedTicket.id)} className="h-8 text-xs gap-1.5">
                          <ExternalLink className="w-3.5 h-3.5" />
                          Tracking page
                        </Button>
                        <ClickUpTaskButton taskId={selectedTicket.clickup_task_id} />
                        {user?.role === 'admin' && (
                          <div className="flex items-center gap-2">
                            {showDeleteConfirm ? (
                              <div className="flex items-center gap-2 bg-red-50 p-1 rounded-md border border-red-200 animate-in fade-in slide-in-from-right-2">
                                <span className="text-xs font-medium text-red-600 px-1">Sure?</span>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleDeleteTicket(selectedTicket.id)}
                                  className="h-6 px-2 text-xs"
                                >
                                  Yes
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setShowDeleteConfirm(false)}
                                  className="h-6 px-2 text-xs hover:bg-red-100 hover:text-red-700"
                                >
                                  No
                                </Button>
                              </div>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowDeleteConfirm(true)}
                                className="h-8 w-8 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 rounded-full text-gray-400 hover:text-gray-900"
                          onClick={() => setViewDialogOpen(false)}
                        >
                          <span className="sr-only">Close</span>
                          <XCircle className="w-5 h-5" />
                        </Button>
                      </div>
                    </div>
                    {isEditingTitle ? (
                      <div className="flex items-center gap-2 mt-1 pr-4">
                        <Input
                          value={editedTitle}
                          onChange={(e) => setEditedTitle(e.target.value)}
                          className="text-2xl font-bold h-10 w-full"
                          autoFocus
                        />
                        <Button size="sm" onClick={handleUpdateTitle} className="bg-black hover:bg-gray-800">Save</Button>
                        <Button variant="ghost" size="sm" onClick={() => { setIsEditingTitle(false); setEditedTitle(selectedTicket.title) }}>Cancel</Button>
                      </div>
                    ) : (
                      <DialogTitle className="text-2xl font-bold text-gray-900 leading-tight pr-4 flex items-center gap-2 group">
                        {selectedTicket.title}
                        {(selectedTicket.created_by === user?.id || isAdminOrSubadmin) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setIsEditingTitle(true)}
                            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Edit className="w-4 h-4 text-gray-400 hover:text-gray-900" />
                          </Button>
                        )}
                      </DialogTitle>
                    )}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 pt-3 border-t border-gray-100">
                    <div className="space-y-1">
                      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Status</span>
                      <div>
                        {isAdminOrSubadmin ? (
                          <Select value={selectedTicket.status} onValueChange={(value) => handleUpdateStatus(selectedTicket.id, value)}>
                            <SelectTrigger className="h-8 w-full text-xs border-gray-200 hover:border-gray-300 focus:ring-0">{getStatusBadge(selectedTicket.status)}</SelectTrigger>
                            <SelectContent align="start">
                              <SelectItem value="open">{getStatusBadge('open')}</SelectItem>
                              <SelectItem value="in_progress">{getStatusBadge('in_progress')}</SelectItem>
                              <SelectItem value="resolved">{getStatusBadge('resolved')}</SelectItem>
                              <SelectItem value="closed">{getStatusBadge('closed')}</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (getStatusBadge(selectedTicket.status))}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Priority</span>
                      <div>
                        {(selectedTicket.created_by === user?.id || isAdminOrSubadmin) ? (
                          <Select value={selectedTicket.priority} onValueChange={(value) => handleUpdateTicketField(selectedTicket.id, 'priority', value)}>
                            <SelectTrigger className="h-8 w-full text-xs border-gray-200 hover:border-gray-300 focus:ring-0">{getPriorityBadge(selectedTicket.priority)}</SelectTrigger>
                            <SelectContent align="start">
                              <SelectItem value="low">{getPriorityBadge('low')}</SelectItem>
                              <SelectItem value="medium">{getPriorityBadge('medium')}</SelectItem>
                              <SelectItem value="high">{getPriorityBadge('high')}</SelectItem>
                              <SelectItem value="critical">{getPriorityBadge('critical')}</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (getPriorityBadge(selectedTicket.priority))}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Due Date</span>
                      <div>
                        {(selectedTicket.created_by === user?.id || isAdminOrSubadmin) ? (
                          <Input type="date" value={selectedTicket.due_date ? selectedTicket.due_date.substring(0, 10) : ''} onChange={(e) => handleUpdateTicketField(selectedTicket.id, 'due_date', e.target.value)} className="h-8 w-full text-xs border-gray-200 hover:border-gray-300 focus:ring-0" />
                        ) : (
                          <span className="text-xs text-gray-600 h-8 flex items-center">{selectedTicket.due_date ? new Date(selectedTicket.due_date).toLocaleDateString() : '—'}</span>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1 col-span-2 sm:col-span-3 lg:col-span-4">
                      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Problem</span>
                      <div>
                        {(selectedTicket.created_by === user?.id || isAdminOrSubadmin) ? (
                          <ProblemTypeSearch value={selectedTicket.problem_type} onChange={(ptValue) => handleUpdateTicketField(selectedTicket.id, 'problem_type', ptValue)} placeholder="Set problem type..." allowCreate={true} inputClassName="h-8 text-xs border-gray-200 hover:border-gray-300 focus-visible:ring-0" />
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {selectedTicket.problem_type?.split(',').map(pt => (
                              <Badge key={pt} variant="outline" className="text-xs bg-gray-50">{pt.trim()}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {isAdminOrSubadmin && (
                      <div className="space-y-1">
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Company</span>
                        <div>
                          <CompanySearch value={selectedTicket.company_id || null} onChange={(id) => handleUpdateTicketField(selectedTicket.id, 'company_id', id || null)} companies={companies} placeholder="Search company..." inputClassName="h-8 text-xs border-gray-200 hover:border-gray-300 focus-visible:ring-0" />
                        </div>
                      </div>
                    )}
                    {isAdminOrSubadmin && (
                      <div className="space-y-1">
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Requested By</span>
                        <div>
                          <RequesterSearch value={selectedTicket.requested_by || null} onChange={(id) => handleUpdateTicketField(selectedTicket.id, 'requested_by', id || null)} placeholder="Search requester..." inputClassName="h-8 text-xs border-gray-200 hover:border-gray-300 focus-visible:ring-0" />
                        </div>
                      </div>
                    )}
                    {getTicketHoursIncludingSubtasks(selectedTicket) > 0 && (
                      <div className="space-y-1">
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Time</span>
                        <div className="flex items-center gap-1.5 text-xs font-medium text-orange-700 bg-orange-50 px-2.5 py-1.5 rounded-md border border-orange-100 h-8">
                          <Clock className="w-3.5 h-3.5" />{getTicketHoursIncludingSubtasks(selectedTicket)}h total
                        </div>
                      </div>
                    )}
                  </div>
                  {(isAdminOrSubadmin || selectedTicket.created_by === user?.id) && selectedTicket.status !== 'closed' && (
                    <div className="pt-2">
                      <Button size="sm" onClick={() => handleUpdateStatus(selectedTicket.id, 'closed')} className="bg-green-600 hover:bg-green-700 text-white font-bold shadow-sm uppercase tracking-wider text-[10px] h-7">
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Mark as Closed
                      </Button>
                    </div>
                  )}
                </DialogHeader>
              </div>

              <div className="p-4 sm:p-6 space-y-8">
                  {/* Description Section */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block">Description</Label>
                      {(selectedTicket.created_by === user?.id || isAdminOrSubadmin) && (
                        !isEditingDescription && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setIsEditingDescription(true)}
                            className="h-6 px-2 text-xs text-gray-500 hover:text-gray-900"
                          >
                            <Edit className="w-3 h-3 mr-1" /> Edit
                          </Button>
                        )
                      )}
                    </div>

                    {isEditingDescription ? (
                      <div className="space-y-2">
                        <Textarea
                          value={editedDescription}
                          onChange={(e) => setEditedDescription(e.target.value)}
                          rows={6}
                          className="bg-white"
                        />
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => setIsEditingDescription(false)}>Cancel</Button>
                          <Button size="sm" onClick={handleUpdateDescription} className="bg-black hover:bg-gray-800">Save</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                        <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{selectedTicket.description}</p>
                      </div>
                    )}
                  </div>

                  {isAdminOrSubadmin && (
                    <>
                      {/* ===== SUBTASKS SECTION ===== */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3" />
                            Subtasks
                            {selectedTicket.subtasks?.length > 0 && (
                              <span className="text-xs font-normal text-gray-400 normal-case">
                                ({selectedTicket.subtasks.filter(s => s.status === 'done').length}/{selectedTicket.subtasks.length} done)
                              </span>
                            )}
                          </Label>
                          <button
                            onClick={() => setShowSubtaskForm(!showSubtaskForm)}
                            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 transition-colors"
                          >
                            <Plus className="w-3 h-3" /> Add
                          </button>
                        </div>

                        {/* Progress bar */}
                        {selectedTicket.subtasks?.length > 0 && (
                          <div className="mb-3">
                            <div className="w-full bg-gray-100 rounded-full h-1.5">
                              <div
                                className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${(selectedTicket.subtasks.filter(s => s.status === 'done').length / selectedTicket.subtasks.length) * 100}%` }}
                              />
                            </div>
                          </div>
                        )}

                        {/* Subtask list */}
                        {selectedTicket.subtasks?.length > 0 && (
                          <div className="space-y-1 mb-2">
                            {selectedTicket.subtasks.map(subtask => (
                              <div key={subtask.id} className="group flex items-start gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                                {editingSubtaskId === subtask.id ? (
                                  <div className="flex-1 space-y-3 bg-white border border-gray-200 p-3 rounded-lg shadow-sm">
                                    <div className="space-y-1">
                                      <Label className="text-xs font-semibold text-gray-700">Subtask Title <span className="text-red-500">*</span></Label>
                                      <Input
                                        value={editingSubtaskForm.title}
                                        onChange={e => setEditingSubtaskForm({ ...editingSubtaskForm, title: e.target.value })}
                                        className="h-8 text-sm"
                                        placeholder="Enter subtask title..."
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="text-xs font-semibold text-gray-700">Description / Comment (Optional)</Label>
                                      <Input
                                        value={editingSubtaskForm.description || ''}
                                        onChange={e => setEditingSubtaskForm({ ...editingSubtaskForm, description: e.target.value })}
                                        className="h-8 text-xs bg-white"
                                        placeholder="Add instructions or details..."
                                      />
                                    </div>
                                    <div className="flex gap-3">
                                      <div className="flex-1 space-y-1">
                                        <Label className="text-xs font-semibold text-gray-700">Assignee (Optional)</Label>
                                        <Select
                                          value={editingSubtaskForm.assignee_email || 'unassigned'}
                                          onValueChange={val => setEditingSubtaskForm({ ...editingSubtaskForm, assignee_email: val === 'unassigned' ? '' : val })}
                                        >
                                          <SelectTrigger className="h-8 text-xs bg-white rounded-md shadow-sm border-gray-200">
                                            <SelectValue placeholder="No assignee" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="unassigned">No assignee</SelectItem>
                                            {user && !teamMembers.find(m => m.email === user.email) && (
                                              <SelectItem value={user.email}>Assign to myself ({user.email})</SelectItem>
                                            )}
                                            {teamMembers.map(m => (
                                              <SelectItem key={m.id} value={m.email}>{m.email === user?.email ? 'Assign to myself' : (m.full_name || m.email)}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <div className="flex-1 space-y-1">
                                        <Label className="text-xs font-semibold text-gray-700">Due Date (Optional)</Label>
                                        <Input
                                          type="date"
                                          value={editingSubtaskForm.due_date ? editingSubtaskForm.due_date.split('T')[0] : ''}
                                          onChange={e => setEditingSubtaskForm({ ...editingSubtaskForm, due_date: e.target.value })}
                                          className="h-8 text-xs bg-white"
                                        />
                                      </div>
                                    </div>
                                    <div className="flex gap-3">
                                      <div className="flex-1 space-y-1">
                                        <Label className="text-xs font-semibold text-gray-700">Time Estimate (Hours)</Label>
                                        <Input
                                          type="number"
                                          step="0.5"
                                          min="0"
                                          value={editingSubtaskForm.time_estimate || ''}
                                          onChange={e => setEditingSubtaskForm({ ...editingSubtaskForm, time_estimate: e.target.value })}
                                          className="h-8 text-xs bg-white"
                                          placeholder="e.g. 2.5"
                                        />
                                      </div>
                                      <div className="flex-1 space-y-1">
                                        <Label className="text-xs font-semibold text-gray-700">Problem Type</Label>
                                        <ProblemTypeSearch
                                          value={editingSubtaskForm.problem_type || ''}
                                          onChange={(ptValue) => setEditingSubtaskForm({ ...editingSubtaskForm, problem_type: ptValue })}
                                          placeholder="Select type..."
                                          allowCreate={true}
                                        />
                                      </div>
                                    </div>
                                    <div className="flex gap-3">
                                      <div className="flex-1 space-y-1">
                                        <Label className="text-xs font-semibold text-gray-700">Status</Label>
                                        <Select
                                          value={editingSubtaskForm.status || ''}
                                          onValueChange={val => setEditingSubtaskForm({ ...editingSubtaskForm, status: val })}
                                        >
                                          <SelectTrigger className="h-8 text-xs bg-white rounded-md shadow-sm border-gray-200">
                                            <SelectValue placeholder="Select status" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="open">Open</SelectItem>
                                            <SelectItem value="in_progress">In Progress</SelectItem>
                                            <SelectItem value="done">Done</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    </div>
                                    <div className="pt-2 flex gap-2 border-t border-gray-100">
                                      <Button size="sm" className="h-8 px-4 text-xs bg-black text-white" onClick={() => handleUpdateSubtask(subtask.id)}>Save Changes</Button>
                                      <Button size="sm" variant="outline" className="h-8 px-4 text-xs" onClick={() => setEditingSubtaskId(null)}>Cancel</Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex-1 flex flex-col min-w-0">
                                    {/* Top row: status toggle + title + actions */}
                                    <div className="flex items-start gap-2">
                                      <button onClick={() => handleToggleSubtaskStatus(subtask)} className="mt-0.5 flex-shrink-0">
                                        {subtask.status === 'done' ? (
                                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                                        ) : subtask.status === 'in_progress' ? (
                                          <Clock className="w-4 h-4 text-yellow-500" />
                                        ) : (
                                          <div className="w-4 h-4 rounded border-2 border-gray-300" />
                                        )}
                                      </button>
                                      <div className="flex-1 min-w-0">
                                        <span className={`text-sm ${subtask.status === 'done' ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                                          {subtask.title}
                                        </span>
                                        <div className="flex items-center gap-2 mt-0.5">
                                          {subtask.assignee_name && (
                                            <span className="text-xs text-gray-400">{subtask.assignee_name}</span>
                                          )}
                                          {subtask.due_date && (
                                            <span className="text-xs text-gray-400">{new Date(subtask.due_date).toLocaleDateString()}</span>
                                          )}
                                          {subtask.status === 'in_progress' && (
                                            <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 rounded-full">In Progress</span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity flex-shrink-0">
                                        <ClickUpTaskButton taskId={subtask.clickup_subtask_id} iconOnly className="h-6 w-6 p-0" />
                                        <button
                                          onClick={() => { 
                                            setEditingSubtaskId(subtask.id); 
                                            setEditingSubtaskForm({ 
                                              title: subtask.title, 
                                              description: subtask.description || '',
                                              assignee_email: subtask.assignee_email || '', 
                                              due_date: subtask.due_date || '',
                                              time_estimate: subtask.time_estimate || '',
                                              problem_type: subtask.tags && subtask.tags.length > 0 ? subtask.tags[0] : '',
                                              status: subtask.status || 'open'
                                            }); 
                                          }}
                                          className="p-1 hover:bg-gray-200 rounded"
                                        >
                                          <Edit className="w-3 h-3 text-gray-500" />
                                        </button>
                                        <button onClick={() => handleDeleteSubtask(subtask.id)} className="p-1 hover:bg-red-100 rounded">
                                          <Trash2 className="w-3 h-3 text-red-400" />
                                        </button>
                                      </div>
                                    </div>
                                    {/* Subtask Comments — full width row below */}
                                    <div className="mt-2 border-t border-gray-100 pt-1.5">
                                      <button
                                        className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
                                        onClick={() => setExpandedSubtaskComments(prev => ({ ...prev, [subtask.id]: !prev[subtask.id] }))}
                                      >
                                        <MessageSquare className="w-3 h-3" />
                                        {subtask.comments?.length > 0 ? `${subtask.comments.length} comment${subtask.comments.length > 1 ? 's' : ''}` : 'Add comment'}
                                      </button>
                                      {expandedSubtaskComments[subtask.id] && (
                                        <div className="mt-2 space-y-2">
                                          {(subtask.comments || []).map(c => (
                                            <div key={c.id} className="bg-white border border-gray-100 rounded-md px-2.5 py-1.5">
                                              <p className="text-xs text-gray-800 whitespace-pre-wrap">{c.text}</p>
                                              <p className="text-[10px] text-gray-400 mt-0.5">{c.created_by_email} · {new Date(c.created_at).toLocaleString()}</p>
                                            </div>
                                          ))}
                                          <div className="flex gap-2">
                                            <Input
                                              placeholder="Write a comment..."
                                              className="h-7 text-xs flex-1"
                                              value={subtaskCommentText[subtask.id] || ''}
                                              onChange={e => setSubtaskCommentText(prev => ({ ...prev, [subtask.id]: e.target.value }))}
                                              onKeyDown={e => { if (e.key === 'Enter') handleAddSubtaskComment(subtask.id); }}
                                            />
                                            <Button
                                              size="sm"
                                              className="h-7 px-2 text-xs bg-black text-white"
                                              onClick={() => handleAddSubtaskComment(subtask.id)}
                                              disabled={subtaskCommentLoading[subtask.id]}
                                            >
                                              {subtaskCommentLoading[subtask.id] ? '...' : 'Post'}
                                            </Button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Add subtask form */}
                        {showSubtaskForm && (
                          <div className="border border-gray-200 rounded-lg p-4 space-y-4 bg-gray-50/50 mt-4 mb-2 shadow-sm">
                            <h4 className="text-sm font-semibold text-gray-800">Create New Subtask</h4>
                            <div className="space-y-1">
                              <Label className="text-xs font-semibold text-gray-700">Subtask Title <span className="text-red-500">*</span></Label>
                              <Input
                                placeholder="Enter a brief title for this subtask..."
                                value={subtaskInput}
                                onChange={e => setSubtaskInput(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleAddSubtask(); if (e.key === 'Escape') setShowSubtaskForm(false); }}
                                className="h-9 text-sm"
                                autoFocus
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs font-semibold text-gray-700">Description / Comment (Optional)</Label>
                              <Input
                                placeholder="Add instructions or details..."
                                value={subtaskDescription}
                                onChange={e => setSubtaskDescription(e.target.value)}
                                className="h-9 text-xs bg-white"
                              />
                            </div>
                            <div className="flex gap-3">
                              <div className="flex-1 space-y-1">
                                <Label className="text-xs font-semibold text-gray-700">Assignee (Optional)</Label>
                                <Select
                                  value={subtaskAssignee || 'unassigned'}
                                  onValueChange={val => setSubtaskAssignee(val === 'unassigned' ? '' : val)}
                                >
                                  <SelectTrigger className="h-9 text-xs bg-white rounded-md shadow-sm border-gray-200">
                                    <SelectValue placeholder="No assignee" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="unassigned">No assignee</SelectItem>
                                    {user && !teamMembers.find(m => m.email === user.email) && (
                                      <SelectItem value={user.email}>Assign to myself ({user.email})</SelectItem>
                                    )}
                                    {teamMembers.map(m => (
                                      <SelectItem key={m.id} value={m.email}>{m.email === user?.email ? 'Assign to myself' : (m.full_name || m.email)}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="flex-1 space-y-1">
                                <Label className="text-xs font-semibold text-gray-700">Due Date (Optional)</Label>
                                <Input
                                  type="date"
                                  value={subtaskDueDate}
                                  onChange={e => setSubtaskDueDate(e.target.value)}
                                  className="h-9 text-xs bg-white"
                                />
                              </div>
                            </div>
                            <div className="flex gap-3">
                              <div className="flex-1 space-y-1">
                                <Label className="text-xs font-semibold text-gray-700">Time Estimate (Hours)</Label>
                                <Input
                                  type="number"
                                  step="0.5"
                                  min="0"
                                  value={subtaskTimeEstimate}
                                  onChange={e => setSubtaskTimeEstimate(e.target.value)}
                                  className="h-9 text-xs bg-white"
                                  placeholder="e.g. 2.5"
                                />
                              </div>
                              <div className="flex-1 space-y-1">
                                <Label className="text-xs font-semibold text-gray-700">Problem Type</Label>
                                <ProblemTypeSearch
                                  value={subtaskProblemType}
                                  onChange={(ptValue) => setSubtaskProblemType(ptValue)}
                                  placeholder="Select type..."
                                  allowCreate={true}
                                />
                              </div>
                            </div>
                            <div className="pt-2 flex gap-2 border-t border-gray-200/60">
                              <Button className="h-9 px-5 text-xs bg-black text-white font-medium w-full sm:w-auto" onClick={handleAddSubtask} disabled={subtaskLoading || !subtaskInput.trim()}>
                                {subtaskLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-1" />}
                                {subtaskLoading ? 'Creating...' : 'Create Subtask'}
                              </Button>
                              <Button variant="outline" className="h-9 px-5 text-xs w-full sm:w-auto" onClick={() => setShowSubtaskForm(false)}>Cancel</Button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Time Logs Section */}
                      {getTicketHoursIncludingSubtasks(selectedTicket) > 0 && (
                        <div>
                          <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block flex items-center gap-2">
                            Time Logs
                            <span className="text-xs font-normal text-gray-400 normal-case">(Logged + subtask estimates: {getTicketHoursIncludingSubtasks(selectedTicket)}h)</span>
                          </Label>
                          <div className="bg-white border boundary-gray-200 rounded-lg divide-y divide-gray-100">
                            {selectedTicket.time_logs && selectedTicket.time_logs.length > 0 && (
                              <div className="max-h-40 overflow-y-auto">
                                {selectedTicket.time_logs.map((log) => (
                                  <div key={log.id} className="p-3 text-sm hover:bg-gray-50 transition-colors group/log">
                                    {editingTimeLogId === log.id ? (
                                      <div className="space-y-2">
                                        <div className="flex gap-2">
                                          <Input
                                            type="number"
                                            step="0.1"
                                            value={editingTimeLogForm.hours}
                                            onChange={(e) => setEditingTimeLogForm({ ...editingTimeLogForm, hours: e.target.value })}
                                            className="w-24 h-8 text-sm"
                                            placeholder="Hours"
                                          />
                                          <Input
                                            value={editingTimeLogForm.description}
                                            onChange={(e) => setEditingTimeLogForm({ ...editingTimeLogForm, description: e.target.value })}
                                            className="flex-1 h-8 text-sm"
                                          />
                                        </div>
                                        <div className="flex justify-end gap-2">
                                          <Button variant="ghost" size="sm" onClick={() => setEditingTimeLogId(null)} className="h-7 px-2 text-xs">Cancel</Button>
                                          <Button size="sm" onClick={() => handleUpdateTimeLog(log.id)} className="h-7 px-2 text-xs bg-black hover:bg-gray-800">Save</Button>
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        <div className="flex justify-between items-center mb-1">
                                          <div className="flex items-center gap-2">
                                            <span className="font-semibold text-gray-900">{log.hours}h</span>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-5 w-5 p-0 opacity-0 group-hover/log:opacity-100 transition-opacity"
                                              onClick={() => {
                                                setEditingTimeLogId(log.id);
                                                setEditingTimeLogForm({ hours: log.hours, description: log.description });
                                              }}
                                            >
                                              <Edit className="w-3 h-3 text-gray-500" />
                                            </Button>
                                          </div>
                                          <span className="text-xs text-gray-500">{new Date(log.logged_at).toLocaleDateString()}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                          <p className="text-gray-600 truncate flex-1 mr-4">{log.description}</p>
                                          <span className="text-xs text-gray-400 truncate max-w-[120px]">{log.logged_by_email}</span>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <Button
                        data-testid="log-time-button"
                        onClick={() => setTimeLogDialogOpen(true)}
                        className="w-full bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900 shadow-sm"
                      >
                        <Clock className="w-4 h-4 mr-2 text-gray-500" />
                        Log Additional Time
                      </Button>

                      {/* Paid Support Toggle */}
                      <div className={`rounded-lg p-3 border ${selectedTicket.is_paid_support ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            id="is_paid_support_dash"
                            checked={!!selectedTicket.is_paid_support}
                            onChange={(e) => handleUpdateTicketField(selectedTicket.id, 'is_paid_support', e.target.checked)}
                            className="w-4 h-4 accent-emerald-600 cursor-pointer"
                          />
                          <label htmlFor="is_paid_support_dash" className={`text-sm font-medium cursor-pointer ${selectedTicket.is_paid_support ? 'text-emerald-800' : 'text-gray-700'}`}>
                            Paid Support Task
                          </label>
                          {selectedTicket.is_paid_support && (
                            <span className="ml-auto text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">Active</span>
                          )}
                        </div>
                        {selectedTicket.is_paid_support && (
                          <div className="mt-3 flex items-center gap-2">
                            <span className="text-sm text-emerald-700 font-medium">Amount ($)</span>
                            <div className="relative flex-1">
                              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="0.00"
                                className="pl-6 h-8 text-sm w-full"
                                defaultValue={selectedTicket.paid_amount || ''}
                                onBlur={(e) => {
                                  const val = parseFloat(e.target.value) || 0;
                                  if (val !== (selectedTicket.paid_amount || 0)) {
                                    handleUpdateTicketField(selectedTicket.id, 'paid_amount', val);
                                  }
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  <Separator className="bg-gray-100" />

                  {/* Comments Section */}
                  <div>
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 block">Discussion & Updates</Label>

                    {selectedTicket.comments && selectedTicket.comments.length > 0 ? (
                      <div className="space-y-4" data-testid="comments-list">
                        {selectedTicket.comments.map((comment) => (
                          <div key={comment.id} className="flex gap-3 group">
                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 text-xs font-semibold text-gray-500 uppercase border border-gray-200">
                              {comment.created_by_email.substring(0, 2)}
                            </div>
                            <div className="flex-1 space-y-1">
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-900">
                                    {comment.source === 'tracking'
                                      ? (selectedTicket.requested_by_name || 'Requester')
                                      : comment.created_by_email}
                                  </span>
                                  {comment.source === 'tracking' && (
                                    <Badge className="text-[10px] h-5 px-1.5 font-normal bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-50">
                                      Requester via tracking page
                                    </Badge>
                                  )}
                                  {comment.source === 'clickup' && (
                                    <Badge variant="outline" className="text-[10px] h-5 px-1.5 font-normal text-gray-500 bg-gray-50">ClickUp</Badge>
                                  )}
                                  {(comment.created_by === user?.id || isAdminOrSubadmin) && comment.source !== 'clickup' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setEditingCommentId(comment.id);
                                        setEditingCommentText(comment.text);
                                      }}
                                      className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity -ml-1"
                                    >
                                      <Edit className="w-3 h-3 text-gray-400 hover:text-gray-900" />
                                    </Button>
                                  )}
                                </div>
                                <span className="text-xs text-gray-400 tabular-nums">
                                  {new Date(comment.created_at).toLocaleString(undefined, {
                                    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                                  })}
                                </span>
                              </div>

                              {editingCommentId === comment.id ? (
                                <div className="space-y-2 mt-1">
                                  <Textarea
                                    value={editingCommentText}
                                    onChange={(e) => setEditingCommentText(e.target.value)}
                                    rows={3}
                                    className="bg-white text-sm"
                                  />
                                  <div className="flex justify-end gap-2">
                                    <Button variant="ghost" size="sm" onClick={() => setEditingCommentId(null)} className="h-7 text-xs">Cancel</Button>
                                    <Button size="sm" onClick={() => handleUpdateComment(comment.id)} className="h-7 text-xs bg-black hover:bg-gray-800">Save</Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="bg-gray-50 rounded-lg rounded-tl-none p-3 text-sm text-gray-700 whitespace-pre-wrap border border-gray-100 group-hover:border-gray-200 transition-colors">
                                  {comment.text}
                                </div>
                              )}

                              {comment.attachments && comment.attachments.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2 ml-1">
                                  {comment.attachments.map((url, idx) => {
                                    const imageUrl = url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
                                    return (
                                      <a key={idx} href={imageUrl} target="_blank" rel="noopener noreferrer" className="block group/img relative overflow-hidden rounded-md border border-gray-200">
                                        <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/5 transition-colors" />
                                        <img
                                          src={imageUrl}
                                          alt="Attachment"
                                          className="h-16 w-16 object-cover"
                                        />
                                      </a>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                        <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No comments yet.</p>
                      </div>
                    )}
                  </div>

                  <form onSubmit={handleAddComment} className="space-y-3">
                    <Textarea
                      data-testid="comment-input"
                      placeholder="Add a comment..."
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      rows={3}
                    />

                    {/* Attachments Preview */}
                    {commentAttachments.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {commentAttachments.map((url, idx) => {
                          const imageUrl = url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
                          return (
                            <div key={idx} className="relative group">
                              <img src={imageUrl} alt="Preview" className="h-16 w-16 object-cover rounded border" />
                              <button
                                type="button"
                                onClick={() => removeAttachment(idx)}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        <input
                          type="file"
                          id="file-upload"
                          className="hidden"
                          onChange={handleFileSelect}
                          accept="image/*"
                        />
                        <label
                          htmlFor="file-upload"
                          className="cursor-pointer text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 transition-colors"
                          title="Attach Image"
                        >
                          <Paperclip className="w-5 h-5" />
                        </label>
                      </div>

                      <Button
                        type="submit"
                        data-testid="add-comment-button"
                        disabled={commentLoading || (!commentText.trim() && commentAttachments.length === 0)}
                        className="bg-black hover:bg-gray-800"
                      >
                        {commentLoading ? 'Adding...' : 'Add Comment'}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </DialogPrimitive.Content>
            </div>
          </DialogPrimitive.Overlay>
        </DialogPrimitive.Portal>
      </Dialog >

      {/* Time Log Dialog */}
      < Dialog open={timeLogDialogOpen} onOpenChange={setTimeLogDialogOpen} >
        <DialogContent data-testid="time-log-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Log Time</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddTimeLog} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="hours">Hours Spent</Label>
              <Input
                id="hours"
                type="number"
                step="0.1"
                min="0.1"
                data-testid="time-hours-input"
                placeholder="e.g., 2.5"
                value={timeLog.hours}
                onChange={(e) => setTimeLog({ ...timeLog, hours: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="time-description">Description</Label>
              <Textarea
                id="time-description"
                data-testid="time-description-input"
                placeholder="Describe the work done..."
                value={timeLog.description}
                onChange={(e) => setTimeLog({ ...timeLog, description: e.target.value })}
                rows={3}
                required
              />
            </div>

            <Button
              type="submit"
              data-testid="submit-time-log-button"
              disabled={timeLogLoading}
              className="w-full bg-black hover:bg-gray-800"
            >
              {timeLogLoading ? 'Logging...' : 'Log Time'}
            </Button>
          </form>
        </DialogContent>
      </Dialog >

      {/* Inline Add Company Dialog */}
      < Dialog open={addCompanyOpen} onOpenChange={(v) => { setAddCompanyOpen(v); if (!v) setNewCompanyForm({ name: '', url: '', notes: '' }); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" /> Create New Company
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCompanyInline} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black"
                value={newCompanyForm.name}
                onChange={e => setNewCompanyForm({ ...newCompanyForm, name: e.target.value })}
                placeholder="Acme Corp"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Website URL</Label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black"
                value={newCompanyForm.url}
                onChange={e => setNewCompanyForm({ ...newCompanyForm, url: e.target.value })}
                placeholder="https://acme.com"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setAddCompanyOpen(false)}>Cancel</Button>
              <Button type="submit" className="flex-1 bg-black hover:bg-gray-800">Create &amp; Select</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog >
    </div >
  );
};

export default Dashboard;
